import WorkflowController, { ACTIONS, CONTROLS } from "./automation_workflow_controller"

const clone = value => JSON.parse(JSON.stringify(value))
const element = (tag, className = "", text = null) => { const node = document.createElement(tag); node.className = className; if (text !== null) node.textContent = text; return node }
const ICONS = { create_record: "file-circle-plus", update_record: "pen-to-square", email: "envelope", record_email: "file-pdf", notify: "bell", review: "square-check", webhook: "link", channel_sync: "arrows-rotate", if: "code-branch", delay: "clock", wait_until: "calendar", wait_condition: "hourglass-half", approval: "user-check", stop: "circle-check" }

export default class extends WorkflowController {
  static targets = ["entryJson", "entryFields", "entrySection", "triggerPanel", "storage", "triggerKind", "resource", "eventType", "eventFields", "frequency", "scheduleGroup", "scheduleField", "resetField", "name", "saveStatus", "status", "saveButton", "selection", "settingsPanel", "workflowTab", "settingsTab", "help", "result", "resultBody", "testQuery", "testRecord", "serverErrors"]
  static values = { timeZone: String, testUrl: String, initialMode: String, selected: String }

  connect() {
    this.submitting = false
    this.unsavedOnConnect = this.hasServerErrorsTarget
    this.sequence = 0
    this.prefix = `editor_${crypto.randomUUID().slice(0, 8)}`
    this.nodes = JSON.parse(this.jsonTarget.value || "[]")
    this.entry = JSON.parse(this.entryJsonTarget.value || "{}")
    this.selected = this.selectedValue || null
    this.mode = "workflow"
    this.entryByResource = {}
    this.previousResource = this.resource
    this.refreshTrigger()
    this.renderEntry()
    this.render()
    this.initialState = this.stateKey()
    if (this.hasServerErrorsTarget) { this.saveStatusTarget.textContent = "Changes not saved"; this.serverErrorsTarget.focus() }
    window.addEventListener("beforeunload", this.beforeUnload)
    document.addEventListener("turbo:before-visit", this.beforeVisit)
    this.element.addEventListener("turbo:submit-end", this.submitEnd)
    if (this.initialModeValue === "test") this.test()
    if (this.initialModeValue === "review") this.review()
  }

  disconnect() {
    this.request?.abort()
    window.removeEventListener("beforeunload", this.beforeUnload)
    document.removeEventListener("turbo:before-visit", this.beforeVisit)
    this.element.removeEventListener("turbo:submit-end", this.submitEnd)
  }
  hasUnsavedChanges() { return this.unsavedOnConnect || !!this.initialState && this.stateKey() !== this.initialState }
  beforeUnload = event => {
    if (this.submitting || !this.hasUnsavedChanges()) return
    event.preventDefault(); event.returnValue = ""
  }
  beforeVisit = event => {
    if (!this.submitting && this.hasUnsavedChanges() && !window.confirm("You have unsaved automation changes. Leave this page?")) event.preventDefault()
  }
  submitEnd = event => {
    if (!event.detail.success) { this.submitting = false; this.saveStatusTarget.textContent = "Changes not saved" }
  }
  get form() { return this.element }
  get resource() { return this.hasResourceTarget ? this.resourceTarget.value : "" }
  get mainAction() { return "" }
  get mainCreation() { return "" }
  icon(kind) { const icon = element("i", `fa-solid fa-${ICONS[kind] || kind}`); icon.setAttribute("aria-hidden", "true"); return icon }
  title(node) {
    if (node.label && node.label !== ACTIONS[node.action] && node.label !== CONTROLS[node.type]) return node.label
    if (node.action === "create_record") return `Create ${this.metadata.creations.find(type => type.key === node.settings?.create_type)?.label.toLowerCase() || "a record"}`
    return ACTIONS[node.action] || CONTROLS[node.type] || "Workflow step"
  }
  nodeName(node) { return this.title(node) }
  locate(id, list = this.nodes) {
    for (let index = 0; index < list.length; index++) {
      const node = list[index]
      if (node.id === id) return { node, list, index }
      for (const key of ["then", "else"]) { const found = this.locate(id, node[key] || []); if (found) return found }
    }
    return null
  }
  resourceFor(node) {
    if (node.email_from) return "email"
    if (!node.target || node.target === "trigger") return this.resource
    return this.locate(node.target)?.node.settings?.create_type || this.resource
  }
  stateKey() { return JSON.stringify([...new FormData(this.element).entries()].filter(([key]) => !["authenticity_token", "editor_selected", "record_to_test"].includes(key))) }
  writeState() {
    this.jsonTarget.value = JSON.stringify(this.nodes)
    this.entryJsonTarget.value = JSON.stringify(this.entry)
    this.selectionTarget.value = this.selected || ""
  }
  save() {
    if (this.hasFieldsTarget) this.fieldsTarget.querySelector("[data-step-validation]")?.remove()
    this.writeState()
    this.changed()
    this.updateSummaries()
  }
  changed() {
    if (this.initialState) this.saveStatusTarget.textContent = this.hasUnsavedChanges() ? "Unsaved changes" : "Saved"
    this.reviewState = null
  }
  updateSummaries() {
    this.canvasTarget.querySelectorAll("[data-node-title]").forEach(label => {
      const node = this.locate(label.dataset.nodeTitle)?.node
      if (node) label.textContent = this.title(node)
    })
    this.canvasTarget.querySelectorAll("[data-node-summary]").forEach(label => {
      const node = this.locate(label.dataset.nodeSummary)?.node
      if (node) label.textContent = this.summary(node)
    })
    const trigger = this.canvasTarget.querySelector("[data-trigger-summary]")
    if (trigger) trigger.textContent = this.triggerSummary()
    const context = this.canvasTarget.querySelector("[data-trigger-details]")
    if (context) context.textContent = this.triggerDetails()
  }
  setMode(mode) {
    this.mode = mode
    this.canvasTarget.hidden = mode !== "workflow"
    this.settingsPanelTarget.hidden = mode !== "settings"
    this.resultTarget.hidden = !["test", "review"].includes(mode)
    this.helpTarget.hidden = mode !== "workflow"
    for (const [button, selected] of [[this.workflowTabTarget, mode !== "settings"], [this.settingsTabTarget, mode === "settings"]]) {
      selected ? button.setAttribute("aria-current", "page") : button.removeAttribute("aria-current")
    }
  }
  workflow() { this.setMode("workflow") }
  settings() { this.setMode("settings") }
  render() {
    this.storageTarget.append(this.triggerPanelTarget)
    this.triggerPanelTarget.hidden = true
    this.canvasTarget.replaceChildren()
    const trigger = element("article", "automation-outline-step is-trigger")
    const toggle = this.button("", () => { this.selected = this.selected === "trigger" ? null : "trigger"; this.catalog = null; this.render() }, "automation-node-toggle")
    const text = element("span", "automation-node-copy")
    text.append(element("span", "automation-node-kind", "Trigger"))
    const summary = element("strong", "", this.triggerSummary()); summary.dataset.triggerSummary = "true"
    const context = element("span", "small fg-2", this.triggerDetails()); context.dataset.triggerDetails = "true"
    text.append(summary, context)
    toggle.append(this.icon("bolt"), text, this.icon("chevron-down"))
    toggle.setAttribute("aria-expanded", String(this.selected === "trigger"))
    trigger.append(toggle)
    if (this.selected === "trigger") { this.triggerPanelTarget.hidden = false; trigger.append(this.triggerPanelTarget) }
    this.canvasTarget.append(trigger)
    this.renderList(this.canvasTarget, this.nodes)
    this.writeState()
    this.setMode(this.mode)
  }
  renderList(parent, list, depth = 0) {
    const container = element("div", "automation-flow-list automation-outline-list")
    parent.append(container)
    list.forEach((node, index) => {
      const item = element("article", "automation-outline-step")
      container.append(item)
      item.dataset.nodeId = node.id
      item.classList.toggle("is-selected", this.selected === node.id)
      const row = element("div", "automation-outline-header")
      const toggle = this.button("", () => { this.selected = this.selected === node.id ? null : node.id; this.catalog = null; this.render() }, "automation-node-toggle")
      const text = element("span", "automation-node-copy")
      text.append(element("span", "automation-node-kind", node.type === "action" ? "Action" : node.type === "approval" ? "Approval" : "Flow control"))
      const title = element("strong", "", this.title(node)); title.dataset.nodeTitle = node.id
      const summary = element("span", "small fg-2", this.summary(node)); summary.dataset.nodeSummary = node.id
      text.append(title, summary)
      toggle.append(this.icon(node.action || node.type), text, this.icon("chevron-down"))
      toggle.setAttribute("aria-expanded", String(this.selected === node.id))
      row.append(toggle)
      const controls = element("div", "automation-flow-controls")
      for (const [offset, label, symbol] of [[-1, "up", "↑"], [1, "down", "↓"]]) {
        if (index + offset < 0 || index + offset >= list.length) continue
        const button = this.button(symbol, () => this.move(list, index, offset), "btn btn-sm btn-bd-light")
        button.setAttribute("aria-label", `Move ${this.title(node)} ${label}`); controls.append(button)
      }
      const remove = this.button("×", () => { if (this.selected === node.id) this.selected = null; this.removeNode(list, index) }, "btn btn-sm btn-bd-light")
      remove.setAttribute("aria-label", `Remove ${this.title(node)}`); controls.append(remove)
      row.append(controls); item.append(row)
      if (this.selected === node.id) {
        const body = element("div", "automation-outline-edit"); body.dataset.automationEditorTarget = "fields"
        item.append(body); container.append(item); parent.append(container)
        this.draft = node; this.editing = { list, index, existing: true }; this.previous = this.availableBefore(node.id)
        this.renderFields()
        const footer = element("div", "automation-outline-edit-footer")
        footer.append(this.button("Done", () => this.apply(), "btn btn-outline theme-secondary")); item.append(footer)
      }
      if (["if", "wait_condition", "approval"].includes(node.type)) {
        const branches = element("div", "automation-flow-branches")
        item.append(branches)
        for (const [key, caption] of [["then", node.type === "approval" ? "Approved" : "Conditions met"], ["else", node.type === "if" ? "Conditions not met" : "Rejected or timed out"]]) {
          node[key] ||= []
          const branch = element("section", `automation-flow-branch is-${key}`)
          branch.append(element("strong", "small", caption))
          branches.append(branch)
          this.renderList(branch, node[key], depth + 1)
          branches.append(branch)
        }
        item.append(branches)
      }
      if (!item.parentElement) container.append(item)
    })
    if (this.catalog?.list === list) this.renderCatalog(container, list, this.catalog.index)
    else if (depth <= 4) container.append(this.button("＋ Add step", () => { this.catalog = { list, index: list.length }; this.selected = null; this.render() }, "btn btn-outline theme-primary automation-add-step"))
    if (!container.parentElement) parent.append(container)
  }
  renderCatalog(parent, list, index) {
    const panel = element("section", "automation-step-catalog")
    panel.append(element("h2", "h6", "Add a step"), element("p", "small fg-2", "Choose what happens next. You can configure every step in the workflow."))
    const previous = this.availableForList(list)
    const targets = [{ id: "trigger", source: this.resource }, ...previous.filter(node => node.action === "create_record").map(node => ({id: node.id, source: node.settings?.create_type}))]
    const actions = element("div", "automation-step-catalog-options")
    for (const [key, label] of Object.entries(ACTIONS)) {
      const target = targets.find(target => this.source(target.source).actions.includes(key))
      if (!target) continue
      const button = this.button(label, () => this.addNode(list, index, { type: "action", action: key, target: target.id }), "btn btn-outline theme-secondary")
      button.prepend(this.icon(key)); actions.append(button)
    }
    panel.append(element("h3", "small fw-semibold", "Actions"), actions)
    const controls = element("div", "automation-step-catalog-options mt-3")
    for (const [key, label] of Object.entries(CONTROLS)) {
      if (["if", "wait_condition"].includes(key) && !this.resource && !previous.some(node => ["create_record", "email", "record_email"].includes(node.action))) continue
      const button = this.button(label, () => this.addNode(list, index, { type: key }), "btn btn-outline theme-secondary")
      button.prepend(this.icon(key)); controls.append(button)
    }
    panel.append(element("h3", "small fw-semibold mt-4", "Flow controls"), controls, this.button("Cancel", () => { this.catalog = null; this.render() }, "btn btn-bd-light mt-3"))
    parent.append(panel)
  }
  addNode(list, index, attributes) {
    const count = steps => steps.reduce((sum, node) => sum + 1 + count(node.then || []) + count(node.else || []), 0)
    if (count(this.nodes) >= 40) { this.errorTarget.textContent = "Use at most 40 workflow steps."; this.errorTarget.hidden = false; return }
    const node = { id: `step_${crypto.randomUUID().replaceAll("-", "").slice(0, 16)}`, target: "trigger", settings: {subject: "{{ automation.name }}", body: "", recipient_source: "addresses", recipient_user_ids: [this.metadata.owner], assignee_user_id: this.metadata.owner, deliver_email: false, deliver_notify: false}, ...attributes }
    list.splice(index, 0, node); this.catalog = null; this.selected = node.id; this.save(); this.render()
    this.fieldsTarget.querySelector("select, input")?.focus({ preventScroll: true })
  }
  close(event) { event?.preventDefault(); this.selected = null; this.draft = null; this.catalog = null }
  renderFields() {
    super.renderFields()
    this.writeState()
  }
  renderEntry() {
    this.entryFieldsTarget.replaceChildren()
    this.group(this.entryFieldsTarget, this.entry, this.resource, null, 0, this.triggerKindTarget.value === "event")
  }
  referencesValid(list = this.nodes, available = new Set(["trigger"])) {
    for (const node of list) {
      const references = [node.target, node.attachment_from, node.reply_to_step, node.email_from].filter(Boolean)
      const expressions = JSON.stringify([node.settings, node.fields, node.payload])
      for (const match of expressions.matchAll(/\bsteps\.([a-z][a-z0-9_]*)\./g)) references.push(match[1])
      if (references.some(id => !available.has(id))) return false
      if (["action", "approval"].includes(node.type)) available.add(node.id)
      if (!this.referencesValid(node.then || [], new Set(available)) || !this.referencesValid(node.else || [], new Set(available))) return false
    }
    return true
  }
  move(list, index, offset) { super.move(list, index, offset); this.save() }
  removeNode(list, index) { super.removeNode(list, index); this.save() }
  summary(node) {
    if (node.type === "delay") {
      const minutes = node.minutes ?? 60
      const [amount, unit] = minutes % 1440 === 0 ? [minutes / 1440, "day"] : minutes % 60 === 0 ? [minutes / 60, "hour"] : [minutes, "minute"]
      return `Wait ${amount} ${unit}${amount === 1 ? "" : "s"}`
    }
    if (node.action === "create_record") return `Create ${this.metadata.creations.find(type => type.key === node.settings?.create_type)?.label || "a record"} · Output available to following steps`
    if (["email", "record_email"].includes(node.action)) return `${node.settings?.recipient_source && node.settings.recipient_source !== "addresses" ? this.source(this.resourceFor(node)).recipient_sources.find(pair => pair[1] === node.settings.recipient_source)?.[0] || "Choose recipients" : node.settings?.recipient_emails || "Choose email recipients"}${node.attachment_from || node.settings?.attach_record_pdf || node.action === "record_email" ? " · With attachment" : ""}`
    return super.summary(node)
  }
  toggle(element, visible) {
    element.hidden = !visible
    element.querySelectorAll("input, select, textarea").forEach(input => { input.disabled = !visible })
  }
  refreshTrigger() {
    const kind = this.triggerKindTarget.value
    for (const option of this.resourceTarget.options) {
      option.disabled = !(this.source(option.value).triggers || ["schedule"]).includes(kind)
      if (!option.value && kind !== "schedule") option.disabled = !(kind === "watch" && this.nodes.some(node => node.action === "create_record" && this.metadata.creations.find(type => type.key === node.settings?.create_type)?.options.includes("report")))
    }
    this.resourceTarget.required = this.resourceTarget.options[0].disabled
    this.resourceTarget.options[0].textContent = kind === "schedule" ? "Organization-wide (no records)" : "Choose a record type"
    this.toggle(this.eventFieldsTarget, kind === "event")
    const events = this.source(this.resource).events || []
    for (const option of this.eventTypeTarget.options) option.disabled = !events.includes(option.value)
    if (this.eventTypeTarget.selectedOptions[0]?.disabled) this.eventTypeTarget.value = events[0] || "created"
    const scheduled = ["schedule", "watch", "date_offset"].includes(kind)
    this.toggle(this.scheduleGroupTarget, scheduled)
    this.scheduleFieldTargets.forEach(field => this.toggle(field, scheduled && field.dataset.frequencies.split(" ").includes(this.frequencyTarget.value)))
    this.toggle(this.entrySectionTarget, !!this.resource)
    const selected = this.resetFieldTarget.value
    this.resetFieldTarget.replaceChildren(new Option("Never reset", ""), ...this.source(this.resource).fields.filter(field => !field[1].startsWith("business.")).map(field => new Option(field[0], field[1])))
    this.resetFieldTarget.value = selected
  }
  resourceChanged() {
    this.entryByResource[this.previousResource] = clone(this.entry)
    this.entry = this.entryByResource[this.resource] || {mode: "all", conditions: [], groups: []}
    this.previousResource = this.resource
    this.renderEntry(); this.triggerChanged()
  }
  triggerChanged(event) {
    if (event?.target === this.triggerKindTarget) {
      if (this.triggerKindTarget.value !== "event") {
        const convert = group => { (group.conditions || []).forEach(row => { if (row.operator === "became_positive") row.value = "0"; row.operator = {changed_to: "eq", changed_to_any: "in", became_positive: "gt"}[row.operator] || row.operator }); (group.groups || []).forEach(convert) }
        convert(this.entry)
      }
      this.renderEntry()
    }
    this.refreshTrigger(); this.save()
  }
  finishTrigger() {
    const invalid = [...this.triggerPanelTarget.querySelectorAll("input, select")].find(input => !input.disabled && !input.checkValidity())
    if (invalid) {
      for (let parent = invalid.parentElement; parent && parent !== this.triggerPanelTarget; parent = parent.parentElement) if (parent.tagName === "DETAILS") parent.open = true
      invalid.reportValidity(); return
    }
    this.selected = null; this.render()
  }
  triggerDetails() { return `${this.resourceTarget.selectedOptions[0]?.textContent || "Your organization"} · ${this.timeZoneValue}` }
  triggerSummary() {
    const kind = this.triggerKindTarget.value
    if (kind === "event") return this.eventTypeTarget.selectedOptions[0]?.textContent || "Choose a record event"
    if (kind === "webhook") return "When a signed partner event arrives"
    const value = id => this.element.querySelector(`#${id}`)?.value
    const time = value("rule_schedule_time") || "Choose a time"
    switch (this.frequencyTarget.value) {
      case "once": return `Once · ${value("rule_schedule_run_at")?.replace("T", " ") || "Choose a date and time"}`
      case "weekly": return `Every ${this.element.querySelector("#rule_schedule_weekday")?.selectedOptions[0]?.textContent} at ${time}`
      case "weekdays": return `Monday–Friday at ${time}`
      case "monthly": return `Day ${value("rule_schedule_day")} of each month at ${time}`
      case "interval": return `Every ${value("rule_schedule_interval_minutes")} minutes`
      default: return `Every day at ${time}`
    }
  }
  submit(event) {
    this.writeState()
    if (!this.nameTarget.checkValidity()) { event.preventDefault(); this.nameTarget.reportValidity(); return }
    this.request?.abort()
    this.submitting = true
    this.saveStatusTarget.textContent = "Saving…"
  }
  test(event) { this.requestPreview("test", event?.currentTarget?.dataset.search === "true") }
  review() { this.requestPreview("review") }
  async requestPreview(mode, search = false) {
    this.writeState()
    if (!this.nameTarget.checkValidity()) { this.nameTarget.reportValidity(); return }
    this.request?.abort(); this.request = new AbortController()
    const recordId = !search && this.hasTestRecordTarget ? this.testRecordTarget.value : ""
    const query = this.hasTestQueryTarget ? this.testQueryTarget.value : ""
    this.setMode(mode)
    this.resultBodyTarget.replaceChildren(element("p", "small fg-2", mode === "test" ? "Testing your saved and unsaved settings…" : "Checking workflow readiness…"))
    const data = new FormData(this.element); data.delete("_method"); data.set("mode", mode)
    if (recordId) data.set("record_id", recordId)
    data.set("test_query", query)
    try {
      const response = await fetch(this.testUrlValue, {method: "POST", body: data, signal: this.request.signal, headers: {Accept: "application/json", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""}})
      const result = await response.json()
      if (result.html && response.ok) {
        this.resultBodyTarget.innerHTML = result.html
        if (mode === "review") this.reviewState = this.stateKey()
      } else {
        const alert = element("div", "alert theme-danger")
        alert.setAttribute("role", "alert"); alert.append(element("strong", "", "Your workflow needs attention"))
        const list = element("ul", "mt-2 mb-0"); (result.errors || ["The workflow could not be checked."]).forEach(message => list.append(element("li", "", message))); alert.append(list)
        this.resultBodyTarget.replaceChildren(alert, this.button("Back to workflow", () => this.workflow(), "btn btn-outline theme-secondary"))
      }
      this.resultTarget.focus({preventScroll: true})
    } catch (error) {
      if (error.name !== "AbortError") this.resultBodyTarget.replaceChildren(element("p", "alert theme-danger", "The workflow could not be checked. Your draft is still here. Try again."), this.button("Back to workflow", () => this.workflow()))
    }
  }
  activate() {
    if (!this.reviewState || this.reviewState !== this.stateKey()) { this.review(); return }
    this.statusTarget.value = "enabled"
    this.element.requestSubmit(this.saveButtonTarget)
  }
}
